import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../component/userContext';
import { API_URL } from '../apiUrl';

const AdminDashboard = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('stats');
    const [stats, setStats] = useState({ users: 0, orders: 0, reviews: 0, revenue: 0 });
    const [users, setUsers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [markup, setMarkup] = useState(5);
    const [loading, setLoading] = useState(true);
    const [syncLoading, setSyncLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    // Pagination states
    const [userPage, setUserPage] = useState(1);
    const [orderPage, setOrderPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        if (!user || user.level !== 'Admin') {
            navigate('/');
            return;
        }
        fetchAdminData();
    }, [user, navigate]);

    const fetchAdminData = async () => {
        setLoading(true);
        try {
            const [statsRes, usersRes, ordersRes, settingsRes] = await Promise.all([
                axios.get(`${API_URL}/api/admin/stats`),
                axios.get(`${API_URL}/api/admin/users`),
                axios.get(`${API_URL}/api/admin/orders`),
                axios.get(`${API_URL}/api/admin/settings`)
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data);
            setOrders(ordersRes.data);
            setMarkup(settingsRes.data.markupPercentage);
        } catch (error) {
            console.error("Admin fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMarkup = async () => {
        setSaveLoading(true);
        try {
            await axios.put(`${API_URL}/api/admin/settings`, { markupPercentage: markup });
            alert("Markup berhasil disimpan!");
        } catch (error) {
            alert("Gagal menyimpan markup");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleSync = async () => {
        if (!window.confirm("Yakin ingin melakukan sinkronisasi harga sekarang? Ini akan mengupdate harga di seluruh website.")) return;
        setSyncLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/admin/sync`);
            alert(`Sinkronisasi Selesai! Berhasil mengupdate ${res.data.updatedCount} produk.`);
            fetchAdminData();
        } catch (error) {
            alert("Sinkronisasi gagal: " + error.response?.data?.error || error.message);
        } finally {
            setSyncLoading(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price);
    };

    const handleUpdateUserLevel = async (email, newLevel) => {
        try {
            await axios.put(`${API_URL}/api/admin/user/${encodeURIComponent(email)}/level`, { level: newLevel });
            setUsers(users.map(u => u.email === email ? { ...u, level: newLevel } : u));
            alert("Level berhasil diubah!");
        } catch (error) {
            alert("Gagal mengubah level");
            console.error(error);
        }
    };

    // Pagination Helpers
    const indexOfLastUser = userPage * itemsPerPage;
    const indexOfFirstUser = indexOfLastUser - itemsPerPage;
    const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
    const totalUserPages = Math.ceil(users.length / itemsPerPage);

    const indexOfLastOrder = orderPage * itemsPerPage;
    const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
    const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalOrderPages = Math.ceil(orders.length / itemsPerPage);

    const renderPagination = (currentPage, totalPages, setPageFunc) => {
        if (totalPages <= 1) return null;
        return (
            <div className="d-flex justify-content-center mt-4">
                <nav>
                    <ul className="pagination pagination-sm">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPageFunc(currentPage - 1)}>Sebelumnya</button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setPageFunc(i + 1)}>{i + 1}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPageFunc(currentPage + 1)}>Selanjutnya</button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-teal" role="status"></div>
                <p className="mt-3 text-muted">Memuat data Admin...</p>
            </div>
        );
    }

    return (
        <div id="admindashboard" className="dashboard-container py-5">
            <div className="container">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="fw-bold mb-0">Admin Panel</h2>
                    <button className="btn btn-outline-light btn-sm" onClick={() => navigate('/profile')}>
                        <i className="bi bi-person-circle me-2"></i>Kembali ke Profil
                    </button>
                </div>

                {/* Stats Overview */}
                <div className="row g-3 mb-5">
                    <div className="col-md-3">
                        <div className="stats-card text-center p-4" style={{ background: 'rgba(11, 140, 117, 0.1)', border: '1px solid var(--secondaryColor)' }}>
                            <div className="text-muted small mb-1">Total Pendapatan (Lunas)</div>
                            <h4 className="fw-bold text-success mb-0">{formatPrice(stats.revenue)}</h4>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="stats-card text-center p-4 bg-white border">
                            <div className="text-muted small mb-1">Total Pengguna</div>
                            <h4 className="fw-bold mb-0">{stats.users}</h4>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="stats-card text-center p-4 bg-white border">
                            <div className="text-muted small mb-1">Total Pesanan</div>
                            <h4 className="fw-bold mb-0">{stats.orders}</h4>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="stats-card text-center p-4 bg-white border">
                            <div className="text-muted small mb-1">Total Ulasan</div>
                            <h4 className="fw-bold mb-0">{stats.reviews}</h4>
                        </div>
                    </div>
                </div>

                <div className="dashboard-layout">
                    {/* Sidebar Tabs */}
                    <div className="dashboard-sidebar">
                        <div className={`sidebar-link ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>
                            <i className="bi bi-graph-up-arrow"></i> Ringkasan
                        </div>
                        <div className={`sidebar-link ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                            <i className="bi bi-people-fill"></i> Daftar Pengguna
                        </div>
                        <div className={`sidebar-link ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
                            <i className="bi bi-table"></i> Riwayat Pesanan
                        </div>
                        <div className={`sidebar-link ${activeTab === 'financials' ? 'active' : ''}`} onClick={() => setActiveTab('financials')}>
                            <i className="bi bi-cash-stack"></i> Pengaturan Cuan
                        </div>
                    </div>

                    {/* Main Section */}
                    <div className="dashboard-main-content">
                        {activeTab === 'stats' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-bold mb-4">Aktivitas Terbaru pada Website</h5>
                                <div className="alert alert-info border-0 shadow-sm" style={{ background: 'rgba(11, 140, 117, 0.05)', color: 'var(--textColor)' }}>
                                    <i className="bi bi-megaphone-fill me-2 text-warning"></i>
                                    Selamat datang di Panel Admin Family TopUp. Kelola akun dan margin keuntungan Anda di sini.
                                </div>
                                <div className="mt-4 p-5 text-center bg-white border rounded-4 opacity-75">
                                    <i className="bi bi-bar-chart-line display-1 mb-3"></i>
                                    <p>Pilih menu pada sidebar untuk mengelola data.</p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'users' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-bold mb-4">Pengguna Terdaftar</h5>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle custom-table">
                                        <thead>
                                            <tr>
                                                <th>Email</th>
                                                <th>WA</th>
                                                <th>Level</th>
                                                <th>Bergabung</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentUsers.map(u => (
                                                <tr key={u._id}>
                                                    <td>{u.email}</td>
                                                    <td>{u.whatsapp || '-'}</td>
                                                    <td>
                                                        <select
                                                            className={`form-select form-select-sm shadow-none fw-bold ${u.level === 'Admin' ? 'text-danger' : 'text-secondary'}`}
                                                            style={{ width: '110px', fontSize: '0.85rem' }}
                                                            value={u.level}
                                                            onChange={(e) => {
                                                                if (window.confirm(`Yakin ingin mengubah pangkat ${u.email} menjadi ${e.target.value}?`)) {
                                                                    handleUpdateUserLevel(u.email, e.target.value);
                                                                }
                                                            }}
                                                        >
                                                            <option value="Member">Member</option>
                                                            <option value="Admin">Admin</option>
                                                        </select>
                                                    </td>
                                                    <td className="small">{new Date(u.createdAt).toLocaleDateString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {renderPagination(userPage, totalUserPages, setUserPage)}
                            </div>
                        )}

                        {activeTab === 'orders' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-bold mb-4">Semua Pesanan</h5>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle custom-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Game</th>
                                                <th>Produk</th>
                                                <th>Harga</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentOrders.map(o => (
                                                <tr key={o._id}>
                                                    <td className="small">#{o.id}</td>
                                                    <td>{o.game}</td>
                                                    <td>{o.productName}</td>
                                                    <td>{formatPrice(o.price)}</td>
                                                    <td>
                                                        <span className={`badge ${o.status === 'Berhasil' ? 'bg-success' : o.status === 'Pending' ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                                            {o.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {renderPagination(orderPage, totalOrderPages, setOrderPage)}
                            </div>
                        )}

                        {activeTab === 'financials' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-bold mb-4">Pengaturan Profit (Markup)</h5>
                                <div className="p-4 rounded-4 border bg-white mb-4">
                                    <label className="db-input-label mb-2">Persentase Keuntungan (Markup %)</label>
                                    <div className="input-group mb-3" style={{ maxWidth: '200px' }}>
                                        <input 
                                            type="number" 
                                            className="db-input-dark" 
                                            value={markup}
                                            onChange={(e) => setMarkup(e.target.value)}
                                        />
                                        <span className="input-group-text bg-transparent border-0 text-white">%</span>
                                    </div>
                                    <button 
                                        className="db-btn-save mt-2" 
                                        onClick={handleSaveMarkup}
                                        disabled={saveLoading}
                                    >
                                        {saveLoading ? 'Menyimpan...' : 'Simpan Persentase'}
                                    </button>
                                </div>

                                <div className="p-4 rounded-4" style={{ background: 'rgba(255,193,7,0.05)', border: '1px dashed rgba(255,193,7,0.3)' }}>
                                    <h6 className="fw-bold text-warning mb-3">Sync Harga Ke Website</h6>
                                    <p className="small text-muted mb-4">
                                        Tombol di bawah akan mengambil data harga terbaru dari VIP-Reseller dan otomatis menambahkan markup sebesar **{markup}%** ke seluruh produk Kakak.
                                    </p>
                                    <button 
                                        className="buy-btn w-100" 
                                        style={{ height: '50px' }}
                                        onClick={handleSync}
                                        disabled={syncLoading}
                                    >
                                        {syncLoading ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span>Menyinkronkan...</>
                                        ) : (
                                            'Sync Harga Produk Sekarang'
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
